import {IUiCritArea, OUiCritInit, OUiCritWithInputInit, UiCritArea, UiCritBase} from "/@back@/wsp/widgets/search/uiCrit.js";
import {EItemPropsMultiValued, EOperatorsSimple, ISearchCrit, SearchItemLinks, SearchItemLinksParentsFromSubItem, SearchItemPropsDate, SearchItemPropsNumber, SearchItemPropsRegexp} from "/@lib@/wsp/search.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {LANG} from "/@lib@/commons/lang.js";
import {IEndPoint} from "/@lib@/commons/io/io.js";
import {InputPeriod} from "/@back@/core/widgets/inputPeriod.js";
import {REG} from "/@lib@/commons/registry.js";
import {OItemPointerInit, SrcPointerFastSelect, SrcPointerInline} from "/@back@/wsp/widgets/srcDrawer.js";
import {JItemWedSearchBarParams} from "/@back@/wsp/widgets/wed/wspSearchBar.js";
import {ITEM} from "/@lib@/wsp/item.js";
import {UiCritLinksArea} from "/@back@/wsp/widgets/search/itemCrit.js";


export interface OPropsCrit extends OUiCritInit {
	/** Nom de la propriété */
	propName: string
	/** Par défaut : area.label */
	label?: string
	/** */
	multivalued?: EItemPropsMultiValued
}

abstract class ItemPropsCritBase extends UiCritBase {
	protected param: OPropsCrit;

	protected _initialize(init: OUiCritInit): void {
		super._initialize(init);
	}

	protected getUiCritXmlAtt(): string {
		return this.critArea.getId();
	}
}

/**
 * Recherche full text
 */

/** Recherche full text d'une props : paramètres d'initialisation */
export interface OPropsCritString extends OPropsCrit {
	/** Recherche approchée (accents, ...). True par défaut */
	fuzzy?: boolean
	/** Création libre de la regExp de recherche */
	buildRegExp?: (searchTxt: string | string[], options?: {}) => RegExp
	/** Paramètres de recherche avancée masqués */
	hideAdvancedOptions?: boolean
}

/** Recherche full text d'une props : area */
export class UiPropsCritStringArea extends UiCritArea {
	constructor(id: string, protected params: OPropsCritString, libs?: IEndPoint | IEndPoint[]) {
		super(id, libs);
		this.setCritSgn("string").setReplacePattern(/\bstring\b/);
	}

	buildAbstract(xml: Element, ctx: OUiCritInit, options?: any): HTMLElement | undefined {
		const txt = xml.getAttribute("uiTextSearch") || "";
		const opts = xml.getAttribute("uiOptions") || "";
		return <span class="crit"><span class="critTitle">{this.getLabel(ctx)}</span> contient '<span
			class="critVal">{txt}</span>'{opts.indexOf(';w;') >= 0 ? ', mot entier' : undefined}{opts.indexOf(';c;') >= 0 ? ', casse respectée' : undefined}</span>;
	}

	buildBody(ctx: OUiCritInit): ItemPropsCritString {
		let elt = new ItemPropsCritString(this.params);
		elt.critArea = this;
		return elt;
	}
}

/** Recherche full text d'une props : crit */
export class ItemPropsCritString extends ItemPropsCritBase {
	protected _text: HTMLInputElement;
	protected _ignoreCase: HTMLInputElement | null;
	protected _wholeWord: HTMLInputElement | null;

	constructor(protected param: OPropsCritString) {super();}

	protected _initialize(init: OUiCritInit & OUiCritWithInputInit): void {
		super._initialize(init);
		const sr = this.shadowRoot;
		const icon = this.critArea ? this.critArea.getIcon({reg: this.reg}) : null;
		const img = icon ? <img src={icon}/> : null;
		const head = sr.appendChild(<div class="critHead">
			{img}
			<span class="critTitle">{this.critArea ? this.critArea.getLabel({reg: this.reg}) : ""}</span>
			<input type="search" oninput={this.onChange} placeholder={init.placeholder}/>
			{this.insertAlternativesBtn(init)}
		</div>);
		this._text = head.querySelector("input");
		UiCritBase.initHeadFromArea(this.critArea, {reg: this.reg}, head);

		if (!this.param.hideAdvancedOptions) {
			const optsRoot = sr.appendChild(<div class="h"/>);
			this._ignoreCase = optsRoot.appendChild(<label class="opt"><input type="checkbox" onclick={this.onChange}/>Respect de la casse</label>).firstElementChild as HTMLInputElement;
			this._wholeWord = optsRoot.appendChild(<label class="opt"><input type="checkbox" onclick={this.onChange}/>Mot entier</label>).firstElementChild as HTMLInputElement;
		}
	}

	buildUiXmlCrit(): Element | undefined {
		const crit: Element = super.buildUiXmlCrit();
		if (crit) {
			crit.setAttribute("uiTextSearch", this._text.value);
			let opts: string;
			if (this._ignoreCase?.checked) {
				opts = this._wholeWord?.checked ? ";c;w;" : ";c;";
			} else if (this._wholeWord?.checked) {
				opts = ";w;";
			}
			if (this._ignoreCase?.checked) opts += opts;
			crit.setAttribute("uiOptions", opts);
		}
		return crit;
	}

	async initFromXmlCrit(xml: Element): Promise<this> {
		this._text.value = xml.getAttribute('uiTextSearch') || "";
		const opts = xml.getAttribute('uiOptions') || "";
		if (this._ignoreCase) this._ignoreCase.checked = opts.indexOf(';c;') >= 0;
		if (this._wholeWord) this._wholeWord.checked = opts.indexOf(';w;') >= 0;
		return this;
	}

	buildCrit(): ISearchCrit | undefined {
		const v = this._text?.value;
		let regExp: RegExp;

		if (this.param.buildRegExp)
			regExp = this.param.buildRegExp(v, {ignoreCase: this._ignoreCase?.checked, wholeWord: this._wholeWord?.checked});
		else if (v) {
			let strPattern = this.param.fuzzy === false ? LANG.escape4Regexp(v) : LANG.escape4RegexpFuzzy(v);
			regExp = new RegExp(this._wholeWord?.checked ? strPattern : '.*' + strPattern + '.*', this._ignoreCase?.checked ? "i" : "")
		}
		return regExp ? new SearchItemPropsRegexp(this.param.propName || this.critArea.getId(), regExp, this.param.multivalued) : undefined;
	}
}

customElements.define("wsp-itemcrit-props-string", ItemPropsCritString);


/**
 * Recherche via une regExp prédéfinie (et non éditale)
 */
export interface OPropsCritFixedRegExp extends OPropsCrit {
	/** RegExp de recherche */
	regExp: RegExp
}

export class UiPropsCritFixedRegExpArea extends UiCritArea {
	constructor(id: string, protected params: OPropsCritFixedRegExp, libs?: IEndPoint | IEndPoint[]) {
		super(id, libs);
		this.setCritSgn("fixedRegexp").setReplacePattern(/\bfixed\b/);
	}

	buildAbstract(xml: Element, ctx: OUiCritInit, options?: any): HTMLElement | undefined {
		return <span class="crit"><span class="critTitle">{this.getLabel(ctx)}</span></span>;
	}

	buildBody(ctx: OUiCritInit): ItemPropsCritFixedRegExp {
		let elt = new ItemPropsCritFixedRegExp(this.params);
		elt.critArea = this;
		return elt;
	}
}

export class ItemPropsCritFixedRegExp extends ItemPropsCritBase {

	constructor(protected param: OPropsCritFixedRegExp) {super();}

	protected _initialize(init: OUiCritInit & OUiCritWithInputInit): void {
		super._initialize(init);
		const sr = this.shadowRoot;
		const icon = this.critArea ? this.critArea.getIcon({reg: this.reg}) : null;
		const img = icon ? <img src={icon}/> : null;
		const head = sr.appendChild(<div class="critHead">
			{img}
			<span class="critTitle">{this.critArea ? this.critArea.getLabel({reg: this.reg}) : ""}</span>
			{this.insertAlternativesBtn(init)}
		</div>);
		const optsRoot = sr.appendChild(<div class="h"/>);
		UiCritBase.initHeadFromArea(this.critArea, {reg: this.reg}, head);
	}

	async initFromXmlCrit(xml: Element): Promise<this> {
		return this;
	}

	buildCrit(): ISearchCrit | undefined {
		return new SearchItemPropsRegexp(this.param.propName || this.critArea.getId(), this.param.regExp, this.param.multivalued);
	}

}

customElements.define("wsp-itemcrit-props-fixed-regexp", ItemPropsCritFixedRegExp);


/**
 * Recherche enum
 */

interface OPropsCritEnumEntry {
	type?: 'sub'
	label: string,
	icon?: string,
	description?: string,
	enums?: Dict<OPropsCritEnumEntry>
}

/** Recherche enum d'une props : paramètres d'initialisation */
export interface OPropsCritEnum extends OPropsCrit {
	enums: Dict<OPropsCritEnumEntry>;
	/** false par défaut */
	multiple?: boolean;
	/** Recherche approchée (accents, ...). True par défaut */
	fuzzy?: boolean
	/** Création libre de la regExp de recherche */
	buildRegExp?: (searchTxt: string | string[], options?: {}) => RegExp
}

/** Recherche enum d'une props : area */
export class UiPropsCritEnumArea extends UiCritArea {

	constructor(id: string, protected params: OPropsCritEnum, libs?: IEndPoint | IEndPoint[]) {
		super(id, libs);
		this.setCritSgn("enum").setReplacePattern(/\benum\b/);
	}

	buildAbstract(xml: Element, ctx: OUiCritInit, options?: any): HTMLElement | undefined {
		const keys = xml ? (xml.getAttribute('uiTextSearch') || "").split("|") : [];
		const opts = xml.getAttribute("uiOptions") || "";
		const labels: string[] = [];
		keys.forEach((key) => {
			const dataEntry = this.params.enums && this.params.enums[key] ? this.params.enums[key] : null;
			labels.push(dataEntry ? dataEntry.label : key)
		});
		let valTag = <span class="critVal">{labels.join(", ")}</span>;
		return <span class="crit"><span class="critTitle">{this.getLabel(ctx)}</span> contient '{valTag}'</span>;
	}

	buildBody(ctx: OUiCritInit): ItemPropsCritEnum {
		let elt = new ItemPropsCritEnum(this.params);
		elt.critArea = this;
		return elt;
	}
}

/** Recherche enum d'une props : crit */
export class ItemPropsCritEnum extends ItemPropsCritBase {
	private _selectElt: HTMLSelectElement;

	constructor(protected param: OPropsCritEnum) {super();}

	protected _initialize(init: OUiCritInit): void {
		super._initialize(init);
		const sr = this.shadowRoot;

		this._selectElt = <select name={name} onchange={this.onChange}/> as HTMLSelectElement;
		this._selectElt.multiple = this.param.multiple ? true : false;

		function appendSubs(parent: Element, enums: Dict<OPropsCritEnumEntry>): void {
			for (const key in enums) {
				const data = enums[key];
				const option = data.type === 'sub' ?
					parent.appendChild(<optgroup label={data.label}/>) :
					parent.appendChild(<option value={key}>{data.label}</option>);
				if (data.description) option.title = data.description;
				if (data.type === 'sub')
					appendSubs(option, data.enums);
			}
		}

		appendSubs(this._selectElt, this.param.enums);
		const icon = this.critArea ? this.critArea.getIcon({reg: this.reg}) : null;
		const img = icon ? <img src={icon}/> : null;
		const head = sr.appendChild(<div class="critHead">
			{img}
			<span class="critTitle">{this.critArea ? this.critArea.getLabel({reg: this.reg}) : ""}</span>
			{this._selectElt}
			{this.insertAlternativesBtn(init)}
		</div>);
		UiCritBase.initHeadFromArea(this.critArea, {reg: this.reg}, head);
	}

	buildUiXmlCrit(): Element | undefined {
		const crit: Element = super.buildUiXmlCrit();
		if (crit) {
			let v: string[] = [];
			if (this._selectElt.selectedOptions) {
				for (const o of this._selectElt.selectedOptions)
					v.push(o.value)
			}
			crit.setAttribute("uiTextSearch", v.join("|"));
		}
		return crit;
	}

	async initFromXmlCrit(xml: Element): Promise<this> {
		let currentValue = xml ? (xml.getAttribute('uiTextSearch') || "").split("|") : [];
		for (const opt of this._selectElt.options) {
			opt.selected = currentValue.includes(opt.value) ? true : false;
		}
		return this;
	}

	buildCrit(): ISearchCrit | undefined {
		const v: string[] = [];
		if (this._selectElt.selectedOptions) {
			for (const o of this._selectElt.selectedOptions)
				v.push(o.value)
		}
		let regExp: RegExp;

		if (this.param.buildRegExp)
			regExp = this.param.buildRegExp(v, {ignoreCase: false, wholeWord: true});
		else if (v.length > 0) {
			v.forEach((entry, pos, array) =>
				array[pos] = this.param.fuzzy === false ? LANG.escape4Regexp(entry) : LANG.escape4RegexpFuzzy(entry)
			);
			regExp = new RegExp(v.join("|"))
		}
		return regExp ? new SearchItemPropsRegexp(this.param.propName || this.critArea.getId(), regExp, this.param.multivalued) : undefined;
	}
}

customElements.define("wsp-itemcrit-props-enum", ItemPropsCritEnum);
REG.reg.registerSkin("wsp-itemcrit-props-enum", 1, /* language=CSS */ `
	option {
		margin-inline-start: 1em;
	}

	select {
		min-width: 1.5em;
		flex: 1;
		display: inline-block;
		text-overflow: ellipsis;
		font-size: inherit;
	}
`);


/**
 * Recherche number
 */

/** Recherche number d'une props : paramètres d'initialisation */
export interface OPropsCritNumber extends OPropsCrit {
}

/** Recherche number d'une props : area */
export class UiPropsCritNumberArea extends UiCritArea {

	static OP: { [index: string]: any } = {
		//"value" : [longLabel, shortLabel]
		"gt": ["Valeur supérieure à", ">"],
		"gt=": ["Valeur supérieure ou égale à", ">="],
		"lt": ["Valeur inférieure à", "<"],
		"lt=": ["Valeur inférieure ou égale à", "<="],
		"=": ["Valeur égale à", "="],
	};

	constructor(id: string, protected params: OPropsCritNumber, libs?: IEndPoint | IEndPoint[]) {
		super(id, libs);
		this.setCritSgn("number").setReplacePattern(/\bnumber\b/);
	}

	buildAbstract(xml: Element, ctx: OUiCritInit, options?: any): HTMLElement | undefined {
		const operator = <span class="critVal">{UiPropsCritNumberArea.OP[xml.getAttribute("op")][1]}</span>;
		const value = <span class="critVal">{xml.getAttribute("value")}</span>;
		return <span class="crit"><span class="critTitle">{this.getLabel(ctx)}</span> {operator} {value}</span>;
	}

	buildBody(ctx: OUiCritInit): ItemPropsCritNumber {
		let elt = new ItemPropsCritNumber(this.params)
		elt.critArea = this;
		return elt;
	}
}

/** Recherche number d'une props : crit */
export class ItemPropsCritNumber extends ItemPropsCritBase {
	private _opElt: HTMLSelectElement;
	private _opValueElt: HTMLInputElement;

	constructor(protected param: OPropsCritNumber) {super();}

	protected _initialize(init: OUiCritInit): void {
		super._initialize(init);
		const sr = this.shadowRoot;
		this._opElt = <select onchange={this.onChange}>
			<option value="gt" selected>{UiPropsCritNumberArea.OP["gt"][0]}</option>
			<option value="gt=">{UiPropsCritNumberArea.OP["gt="][0]}</option>
			<option value="lt">{UiPropsCritNumberArea.OP["lt"][0]}</option>
			<option value="lt=">{UiPropsCritNumberArea.OP["lt="][0]}</option>
			<option value="=">{UiPropsCritNumberArea.OP["="][0]}</option>
		</select> as HTMLSelectElement;
		this._opValueElt = <input value="0" type="number" min="0" step="1" oninput={this.onChange} onchange={this.onChange}/> as HTMLInputElement;
		const icon = this.critArea ? this.critArea.getIcon({reg: this.reg}) : null;
		const img = icon ? <img src={icon}/> : null;
		const head = sr.appendChild(<div class="critHead">
			{img}
			<span class="critTitle">{this.critArea ? this.critArea.getLabel({reg: this.reg}) : ""}</span>
			<div class="h">
				{this._opElt}
				{this._opValueElt}
			</div>
			{this.insertAlternativesBtn(init)}
		</div>);
		UiCritBase.initHeadFromArea(this.critArea, {reg: this.reg}, head);
	}

	async initFromXmlCrit(xml: Element): Promise<this> {
		if (xml && xml.hasAttribute("op")) this._opElt.value = xml.getAttribute("op");
		if (xml && xml.hasAttribute("value")) this._opValueElt.value = xml.getAttribute("value");
		return this;
	}

	buildCrit(): ISearchCrit | undefined {
		const v = Number.parseInt(this._opValueElt.value);
		if (Number.isInteger(v))
			return new SearchItemPropsNumber(this.param.propName || this.critArea.getId(), this._opElt.value as EOperatorsSimple, v, this.param.multivalued);
		return undefined;
	}
}

customElements.define("wsp-itemcrit-props-number", ItemPropsCritNumber);
REG.reg.registerSkin("wsp-itemcrit-props-number", 1, /* language=CSS */ `
	option {
		margin-inline-start: 1em;
	}

	select {
		min-width: 1.5em;
		flex: 1;
		display: inline-block;
		text-overflow: ellipsis;
	}

	input {
		display: inline-block;
		min-width: 1.5em;
		flex: 1;
	}
`);

/**
 * Recherche date
 */

/** Recherche date d'une props : paramètres d'initialisation */
export interface OPropsCritDate extends OPropsCrit {
	// 'past' par défaut
	format?: 'future,past' | 'past' | 'future'
}

/** Recherche date d'une props : area */
export class UiPropsCritDateArea extends UiCritArea {
	constructor(id: string, protected params: OPropsCritDate, libs?: IEndPoint | IEndPoint[]) {
		super(id, libs);
		this.setCritSgn("date").setReplacePattern(/\bdate\b/);
	}

	buildAbstract(xml: Element, ctx: OUiCritInit, options?: any): HTMLElement | undefined {
		//FIXME : afficher une forme lisible de la date spécifiée
		return <span class="crit"><span class="critTitle">{this.getLabel(ctx)}</span></span>;
	}

	buildBody(ctx: OUiCritInit): ItemPropsCritDate {
		let elt = new ItemPropsCritDate(this.params);
		elt.critArea = this;
		return elt;
	}
}

/** Recherche date d'une props : crit */
export class ItemPropsCritDate extends ItemPropsCritBase {
	protected _dateElt: InputPeriod;

	constructor(protected param: OPropsCritDate) {super();}

	protected _initialize(init: OUiCritInit): void {
		super._initialize(init);
		const sr = this.shadowRoot;
		let modes;
		switch (this.param.format) {
		case "future,past" :
			modes = InputPeriod.buildDefaultModes_futurAndPast();
			break;
		case "future" :
			modes = InputPeriod.buildDefaultModes_futur();
			break;
		}
		this._dateElt = new InputPeriod().initialize({defaultLabel: "Choisir une valeur...", modes});
		this._dateElt.addEventListener("change", this.onChange);
		this._dateElt.addEventListener("input", this.onChange);
		const icon = this.critArea ? this.critArea.getIcon({reg: this.reg}) : null;
		const img = icon ? <img src={icon}/> : null;
		const head = sr.appendChild(<div class="critHead">
			{img}
			<span class="critTitle">{this.critArea ? this.critArea.getLabel({reg: this.reg}) : ""}</span>
			{this._dateElt}
			{this.insertAlternativesBtn(init)}
		</div>);
		UiCritBase.initHeadFromArea(this.critArea, {reg: this.reg}, head);
	}

	buildUiXmlCrit(): Element | undefined {
		const crit: Element = super.buildUiXmlCrit();
		if (crit) {
			if (crit && this._dateElt.isSpecified()) {
				crit.setAttribute("uiCurrentMode", this._dateElt.currentMode.getId());
				// Valeurs brutes (sans les regles d'exclusion/normalisation qui sont fournies par le "currentMode")
				if (this._dateElt.date1.valueAsNumber) crit.setAttribute("uiCurrentLess", this._dateElt.date1.valueAsNumber.toString());
				if (this._dateElt.date2.valueAsNumber) crit.setAttribute("uiCurrentMore", this._dateElt.date2.valueAsNumber.toString());
			}
		}
		return crit;
	}

	async initFromXmlCrit(xml: Element): Promise<this> {
		this._dateElt.reset(xml.getAttribute("uiCurrentMode"), xml.hasAttribute("uiCurrentLess") ? Number.parseInt(xml.getAttribute("uiCurrentLess")) : null, xml.hasAttribute("uiCurrentMore") ? Number.parseInt(xml.getAttribute("uiCurrentMore")) : null);
		return this;
	}

	buildCrit(): ISearchCrit | undefined {
		if (this._dateElt.isSpecified())
			return SearchItemPropsDate.buildFromInputPeriod(this._dateElt, this.param.propName || this.critArea.getId(), this.param.multivalued);
		return undefined;
	}
}

customElements.define("wsp-itemcrit-props-date", ItemPropsCritDate);

/**
 * Recherche ref item
 */

/** Recherche ref item d'une props : paramètres d'initialisation */
export interface OPropsCritRef {
	/** Par défaut : area.label */
	label?: string
	whiteList: {
		itModel: string|RegExp
		itSubModels?:string[]|RegExp
		lnkTraits?: string[]
	}[]
}

/** Recherche ref item d'une props : area */
export class UiPropsCritRefArea extends UiCritArea {
	constructor(id: string, protected params: OPropsCritRef, libs?: IEndPoint | IEndPoint[]) {
		super(id, libs);
		this.setCritSgn("ref");
	}

	buildAbstract(xml: Element, ctx: OUiCritInit, options?: any): HTMLElement | undefined {
		const srcUri = UiCritLinksArea.makeSrcUriFromLinkExp(xml);
		const val = <span class="critVal">{srcUri ? srcUri : 'indéfini'}</span>;
		const result = <span class="crit"><span class="critTitle">{this.getLabel(ctx)}</span> '{val}'</span>;
		if (srcUri) {
			let srcPointer: SrcPointerInline = <SrcPointerInline î={{
				reg: ctx.reg,
			} as OItemPointerInit}/> as SrcPointerInline;
			Promise.resolve(srcPointer.setSrcRef(srcUri)).then(() => {
				val.innerText = "";
				val.appendChild(srcPointer);
			});
		}
		return result;
	}

	buildWedSearch(xml: Element, criterions: Dict<IUiCritArea>): JItemWedSearchBarParams | undefined {
		return {
			searchId: "item",
			findRef: UiCritLinksArea.makeSrcUriFromLinkExp(xml)
		}
	}

	buildBody(ctx: OUiCritInit): ItemPropsCritRef {
		let elt = new ItemPropsCritRef(this.params);
		elt.critArea = this;
		return elt;
	}
}

/** Recherche ref item (parents) */
export class ItemPropsCritRef extends UiCritBase {
	protected itemPtr: SrcPointerInline;

	constructor(protected param: OPropsCritRef) {super();}

	protected _initialize(init: OUiCritInit): void {
		super._initialize(init);
		const sr = this.shadowRoot;
		const sngs: string[] = [];
		const subSngs: string[] = [];

		this.param.whiteList.forEach((entry) => {
			if(typeof entry.itModel==='string'){
				const it = this.reg.env.wsp.wspMetaUi?.getItemType(entry.itModel);
				if (it) sngs.push(".*"+LANG.escape4Regexp(it.getSgn())+"\\b.*")
			}else
				sngs.push(entry.itModel.source)

			if(Array.isArray(entry.itSubModels)){
				entry.itSubModels.forEach(itSubModel=>{
					const it = this.reg.env.wsp.wspMetaUi?.getItemType(itSubModel);
					if (it) subSngs.push(LANG.escape4Regexp(it.getModel()))
				})
			}else
				sngs.push(entry.itSubModels.source)
		});

		this.itemPtr = <SrcPointerInline î={{
			reg: this.reg,
			defaultAction: SrcPointerFastSelect.SINGLETON,
			sgnPattern: new RegExp("(" + sngs.join('|') + ")"),
			subSgnPattern: subSngs.length ? new RegExp("(" + subSngs.join('|') + ")") : undefined,
		} as OItemPointerInit}/> as SrcPointerInline;
		const icon = this.critArea ? this.critArea.getIcon({reg: this.reg}) : null;
		const img = icon ? <img src={icon}/> : null;
		const head = sr.appendChild(<div class="critHead">
			{img}
			<span class="critTitle">{this.critArea ? this.critArea.getLabel({reg: this.reg}) : ""}</span>
			{this.itemPtr}
			{this.insertAlternativesBtn(init)}
		</div>);
		UiCritBase.initHeadFromArea(this.critArea, {reg: this.reg}, head);
		this.itemPtr.onSrcRefChange.add(this.onChange);
	}

	async initFromXmlCrit(xml: Element): Promise<this> {
		const srcUri = UiCritLinksArea.makeSrcUriFromLinkExp(xml);
		if (srcUri) {
			await this.itemPtr.setSrcRef(srcUri);
			if (!this.itemPtr.shortDesc?.srcUri || ITEM.getSrcUriType(this.itemPtr.shortDesc.srcUri) != "item")
				this.itemPtr.setSrcRef(null);
		}
		return this;
	}

	async initFromInsAction(xml?: Element): Promise<this> {
		const srcUri = UiCritLinksArea.makeSrcUriFromLinkExp(xml);
		if (srcUri) {
			await this.itemPtr.setSrcRef(srcUri);
			if (!this.itemPtr.shortDesc?.srcUri || ITEM.getSrcUriType(this.itemPtr.shortDesc.srcUri) != "item") {
				this.itemPtr.setSrcRef(null);
				await this.itemPtr.openFastSelect();
			}
		} else if (!this.itemPtr.srcRef) {
			await this.itemPtr.openFastSelect();
		}
		return this;
	}

	buildCrit(): ISearchCrit | undefined {
		if (this.itemPtr.shortDesc) {
			const whiteElt = this.param.whiteList.find((entry) => {
				if(typeof entry.itModel ==='string' ? entry.itModel === this.itemPtr.shortDesc.itModel : entry.itModel.test(this.itemPtr.shortDesc.itModel)){
					/* TODO eval test subItem
					if(this.itemPtr.shortDesc.itSubItem){
						//...
					}else
						return true
					*/
					return true
				}
				return false
			})

			const exp = this.itemPtr.shortDesc.itSubItem
				? new SearchItemLinksParentsFromSubItem().setRefUri(this.itemPtr.srcRefSub)
				: new SearchItemLinks("LinkParents");

			exp.setPath(this.itemPtr.srcRef);

			if (whiteElt && whiteElt.lnkTraits)
				exp.setLinkTraitFilter(whiteElt.lnkTraits.join('|'))
			return exp;
		}
	}

	protected getUiCritXmlAtt(): string {
		return this.critArea.getId();
	}
}

customElements.define("wsp-itemcrit-props-ref", ItemPropsCritRef);